/**
 * Individual notification item component
 */

import { formatDistanceToNow } from 'date-fns';
import { 
  Bell, 
  Clock, 
  Calendar, 
  FileText, 
  AlertTriangle, 
  CheckCircle,
  Info,
  X,
  MapPin
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { Notification } from '@/services/notifications/notificationService';
import { NOTIFICATION_CATEGORIES, getNotificationCategory } from '@/constants/notificationTypes';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  compact?: boolean;
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case NOTIFICATION_CATEGORIES.ATTENDANCE:
      return Clock;
    case NOTIFICATION_CATEGORIES.TIMESHEET:
      return FileText;
    case NOTIFICATION_CATEGORIES.SHIFT:
      return Calendar;
    case NOTIFICATION_CATEGORIES.LEAVE:
      return Calendar;
    default:
      return Bell;
  }
};

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'error':
      return AlertTriangle;
    case 'warning':
      return AlertTriangle;
    case 'info':
    default:
      return Info;
  }
};

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'error':
      return 'text-destructive';
    case 'warning':
      return 'text-amber-500';
    case 'info':
    default:
      return 'text-primary';
  }
};

export function NotificationItem({ 
  notification, 
  onMarkRead, 
  onDismiss,
  compact = false 
}: NotificationItemProps) {
  const category = getNotificationCategory(notification.type_key);
  const CategoryIcon = getCategoryIcon(category);
  const SeverityIcon = getSeverityIcon(notification.severity);
  const isUnread = !notification.read_at;
  
  const message = typeof notification.payload?.message === 'string' 
    ? notification.payload.message 
    : 'New notification';
  
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });

  // Extract payload with defensive access
  const payload = notification.payload as Record<string, unknown>;
  
  // Summary notification handling (aggregated attendance notifications)
  const isSummary = payload?.is_summary === true;
  const flagCount = typeof payload?.flag_count === 'number' ? payload.flag_count : 0;
  const flags = Array.isArray(payload?.flags) ? payload.flags as Array<{
    type?: string;
    employee_name?: string;
    event_date?: string;
  }> : [];
  
  // Geo context for attendance notifications
  const geofenceLabel = typeof payload?.geofence_label === 'string' ? payload.geofence_label : null;
  const distanceMeters = typeof payload?.distance_meters === 'number' ? payload.distance_meters : null;
  const isWithinRadius = typeof payload?.is_within_radius === 'boolean' ? payload.is_within_radius : null;
  const hasGeoContext = geofenceLabel || distanceMeters !== null;

  const handleClick = () => {
    if (isUnread && onMarkRead) {
      onMarkRead(notification.id);
    }
  };

  return (
    <div 
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer",
        isUnread 
          ? "bg-primary/5 hover:bg-primary/10" 
          : "hover:bg-muted/50",
        compact && "p-2 gap-2"
      )}
      onClick={handleClick}
    >
      {/* Icon */}
      <div className={cn(
        "shrink-0 p-2 rounded-full",
        isUnread ? "bg-primary/10" : "bg-muted"
      )}>
        <CategoryIcon className={cn(
          "h-4 w-4",
          isUnread ? getSeverityColor(notification.severity) : "text-muted-foreground"
        )} />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className={cn(
              "text-sm line-clamp-2",
              isUnread ? "font-medium text-foreground" : "text-muted-foreground"
            )}>
              {message}
            </p>
            
            {/* Summary notification: show flag details when not compact */}
            {isSummary && !compact && flags.length > 0 && flags.length <= 5 && (
              <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                {flags.slice(0, 3).map((flag, idx) => (
                  <li key={idx} className="flex items-center gap-1.5">
                    <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                    {flag.employee_name && <span>{flag.employee_name}:</span>}
                    <span className="capitalize">{flag.type?.replace('_', ' ') || 'Issue'}</span>
                  </li>
                ))}
                {flags.length > 3 && (
                  <li className="text-muted-foreground/70">
                    +{flags.length - 3} more...
                  </li>
                )}
              </ul>
            )}
            
            {/* Summary badge for compact view */}
            {isSummary && compact && flagCount > 1 && (
              <span className="inline-flex items-center text-xs text-muted-foreground mt-0.5">
                ({flagCount} issues)
              </span>
            )}
          </div>
          
          {onDismiss && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 hover:bg-destructive/10 hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(notification.id);
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">{timeAgo}</span>
          {isUnread && (
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          )}
          {/* Geo context badge for attendance notifications */}
          {hasGeoContext && category === NOTIFICATION_CATEGORIES.ATTENDANCE && (
            <span className={cn(
              "inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full",
              isWithinRadius === true 
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : isWithinRadius === false
                  ? "bg-destructive/10 text-destructive"
                  : "bg-muted text-muted-foreground"
            )}>
              <MapPin className="h-3 w-3" />
              {geofenceLabel || (distanceMeters !== null ? `${Math.round(distanceMeters)}m` : 'Unknown')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
